import React from 'react';
import update from 'immutability-helper';
import logger from '../logging';
import {
  Client,
  createValidationContext,
  MemberGroup,
  memberGroupSchema,
  memberGroupSchemaV2,
  SearchGroup,
} from '../models';
import { SsnDatabase } from '../ssn-db';

const log = logger(__filename);

export type Action =
  | { type: 'error'; error?: Error }
  | { type: 'startQuery' }
  | {
      type: 'receivedClientFullTreeResults';
      results: Client;
      selectedMemberGroupId?: string;
      isMembershipSingleSourceOfTruthInEligibility?: boolean;
    }
  | { type: 'receivedUpdateMemberGroupResults'; results: any }
  | { type: 'selectMemberGroup'; memberGroupId: string }
  | { type: 'closeMemberGroup'; shouldDiscardChanges?: boolean }
  | { type: 'updateMemberGroupIsDirty'; isDirty: boolean }
  | { type: 'addMemberGroup' }
  | { type: 'memberSearch'; searchKey: string }
  | { type: 'memberSearchGroupUpdate'; searchGroup: SearchGroup }
  | { type: 'memberSearchSubmit' };

export type Status = 'loading' | 'error' | 'error:forbidden' | 'list' | 'editSelected' | 'newMemberGroup';

export interface State {
  status: Status;
  error?: Error;
  client?: Client;
  isSelectedMemberGroupDirty?: boolean;
  selectedMemberGroup?: MemberGroup;
  ssnDB: SsnDatabase;
  editSerial: number;
  searchKey: string;
  searchKeySubmitted: string;
  searchGroup: SearchGroup;
}

function populateSsnDB(client: Client): SsnDatabase {
  const db = new SsnDatabase();
  for (const mg of client.memberGroups) {
    db.register(mg.primary.ssn, mg.primary.relationshipCode);
    for (const { ssn, relationshipCode } of mg.dependents) {
      db.register(ssn, relationshipCode);
    }
  }

  return db;
}

function promptUserNavigateAway() {
  alert('You have unsaved changes to this member group. Please revert changes before navigating away.');
}

function appBehaviorReducer(state: State, action: Action): State {
  switch (action.type) {
    case 'startQuery':
      return update(state, { status: { $set: 'loading' } });

    case 'selectMemberGroup': {
      if (state.isSelectedMemberGroupDirty) {
        promptUserNavigateAway();
        return state;
      }

      const selectedMemberGroup = state.client!.memberGroups.find(mg => mg.id === action.memberGroupId);
      if (!selectedMemberGroup) return state;

      return update(state, {
        status: { $set: 'editSelected' },
        editSerial: i => i + 1,
        selectedMemberGroup: { $set: selectedMemberGroup },
        isSelectedMemberGroupDirty: { $set: false },
      });
    }

    case 'receivedClientFullTreeResults': {
      const client = action.results;
      if (client == null) return update(state, { status: { $set: 'error:forbidden' } });

      const ssnDB = populateSsnDB(client);

      // Validate all member groups
      client.memberGroups = client.memberGroups.map(mg => {
        const plan = client.plans.find(p => p.id === mg.benefitPlanId);
        const mgSchema = action.isMembershipSingleSourceOfTruthInEligibility ? memberGroupSchemaV2 : memberGroupSchema;
        const result = mgSchema.validate(mg, {
          abortEarly: false,
          allowUnknown: true,
          stripUnknown: true,
          errors: { wrap: { label: false } },
          context: createValidationContext(
            ssnDB,
            plan?.startDate,
            plan?.endDate,
            mg?.primary.benefitStartDate,
            mg?.primary.benefitEndDate,
            false,
            action.isMembershipSingleSourceOfTruthInEligibility,
          ),
        });
        return { ...mg, errors: result.error?.details ?? [], warnings: result.warning?.details ?? [] };
      });

      const selectedMemberGroup = action.selectedMemberGroupId
        ? client.memberGroups.find(mg => mg.id === action.selectedMemberGroupId)
        : undefined;

      return update(state, {
        status: { $set: selectedMemberGroup ? 'editSelected' : 'list' },
        client: { $set: client },
        ssnDB: { $set: ssnDB },
        editSerial: i => i + 1,
        selectedMemberGroup: { $set: selectedMemberGroup },
      });
    }

    case 'closeMemberGroup':
      if (state.isSelectedMemberGroupDirty && !action.shouldDiscardChanges) {
        promptUserNavigateAway();
        return state;
      }

      return update(state, {
        status: { $set: 'list' },
        $unset: ['selectedMemberGroup', 'isSelectedMemberGroupDirty'],
      });

    case 'addMemberGroup':
      if (state.isSelectedMemberGroupDirty) {
        promptUserNavigateAway();
        return state;
      }

      return update(state, {
        status: { $set: 'newMemberGroup' },
        editSerial: i => i + 1,
        $unset: ['selectedMemberGroup'],
      });

    case 'updateMemberGroupIsDirty':
      return update(state, {
        isSelectedMemberGroupDirty: { $set: action.isDirty },
      });

    case 'memberSearch':
      return update(state, {
        searchKey: { $set: action.searchKey },
      });

    case 'memberSearchGroupUpdate':
      return update(state, {
        searchGroup: { $set: action.searchGroup },
      });

    case 'memberSearchSubmit':
      return update(state, {
        searchKeySubmitted: { $set: state.searchKey },
      });

    case 'error':
      log.error({ err: action.error }, 'Received error');
      return update(state, {
        status: { $set: 'error' },
        error: { $set: action.error },
      });

    default:
      log.warn({ strType: action.type }, 'Action not implemented');
      return state;
  }
}

function stateInitializer(): State {
  return {
    status: 'loading',
    ssnDB: new SsnDatabase(),
    editSerial: 0,
    searchKey: '',
    searchKeySubmitted: '',
    searchGroup: SearchGroup.ALL,
  };
}

export function useAppController() {
  return React.useReducer(appBehaviorReducer, undefined, stateInitializer);
}
